import React from 'react';
import { LOCAL_STORAGE_KEYS, useLocalStorage } from 'utils/local-storage';
import { getCurrentCoordinates, isGeolocationSupported } from './geolocation';

/**
 * Manages the user's opt-in "location access" preference and the last known
 * GPS coordinates.
 *
 * The preference is persisted in localStorage so it survives reloads and is
 * shared across the app (form auto-population, settings toggle, login prompt).
 *
 * Everything here is fully optional and backward compatible: users who never
 * enable location access simply keep an `undefined` preference and no
 * coordinates are ever populated.
 */
export function useLocationAccess() {
  const [isEnabled, setIsEnabled] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.LOCATION_ACCESS_ENABLED,
  );
  const [coordinates, setCoordinates] = useLocalStorage<string>(
    LOCAL_STORAGE_KEYS.LOCATION_COORDINATES,
  );

  /**
   * Fetches the current device coordinates and caches them. Safe to call at
   * any time; resolves to the coordinates string or `undefined` on failure.
   */
  const refreshCoordinates = React.useCallback(async (): Promise<
    string | undefined
  > => {
    if (!isGeolocationSupported()) {
      return undefined;
    }

    try {
      const nextCoordinates = await getCurrentCoordinates();
      setCoordinates(nextCoordinates);
      return nextCoordinates;
    } catch {
      // Permission denied / timeout / unsupported - stay silent and keep the
      // app usable without location.
      return undefined;
    }
  }, [setCoordinates]);

  /**
   * Enables location access. This requests the current position (which triggers
   * the browser's native permission prompt) and caches the result.
   */
  const enableLocationAccess = React.useCallback(async (): Promise<void> => {
    setIsEnabled(true);
    await refreshCoordinates();
  }, [refreshCoordinates, setIsEnabled]);

  const disableLocationAccess = React.useCallback((): void => {
    setIsEnabled(false);
  }, [setIsEnabled]);

  return {
    isLocationAccessEnabled: Boolean(isEnabled),
    coordinates,
    enableLocationAccess,
    disableLocationAccess,
    refreshCoordinates,
    isGeolocationSupported: isGeolocationSupported(),
  };
}

